import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { TenantWorkspaceApi, type AvailableVertical, type TenantVertical, type AssignVerticalRequest, type UpdateVerticalRequest } from '@/modules/tenants/services/workspace-api';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { ErrorState } from '@/shared/feedback';
import { FiPlus, FiTrash2, FiStar, FiX, FiRefreshCw, FiAlertCircle, FiPackage, FiCheck } from 'react-icons/fi';

export function VerticalsPage(): ReactNode {
  const [availableVerticals, setAvailableVerticals] = useState<AvailableVertical[]>([]);
  const [assignedVerticals, setAssignedVerticals] = useState<TenantVertical[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const errorMessage = error?.message ?? 'An unknown error occurred';
  const [newVerticalCode, setNewVerticalCode] = useState('');
  const [newVerticalPrimary, setNewVerticalPrimary] = useState(false);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [available, assigned] = await Promise.all([
        TenantWorkspaceApi.listAvailableVerticals(),
        TenantWorkspaceApi.listAssignedVerticals(),
      ]);
      setAvailableVerticals(available);
      setAssignedVerticals(assigned);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load verticals'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAssign = async () => {
    if (!newVerticalCode.trim()) return;
    setIsAssigning(true);
    setError(null);
    try {
      await TenantWorkspaceApi.assignVertical({
        verticalCode: newVerticalCode.trim(),
        isPrimary: newVerticalPrimary,
      });
      setNewVerticalCode('');
      setNewVerticalPrimary(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to assign vertical'));
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUpdate = async (verticalCode: string, input: UpdateVerticalRequest) => {
    setIsUpdating(verticalCode);
    setError(null);
    try {
      await TenantWorkspaceApi.updateVertical(verticalCode, input);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update vertical'));
    } finally {
      setIsUpdating(null);
    }
  };

  const handleSetPrimary = (verticalCode: string) => {
    handleUpdate(verticalCode, { isPrimary: true });
  };

  const handleToggleStatus = (vertical: TenantVertical) => {
    handleUpdate(vertical.verticalCode, { status: vertical.status === 'active' ? 'disabled' : 'active' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return <ErrorState title="Could not load verticals" message={errorMessage} onRetry={loadData} />;
  }

  const assignedPrimary = assignedVerticals.find(v => v.isPrimary);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FiPackage className="h-5 w-5" />
            Business Verticals
          </h2>
          <p className="text-sm text-content-muted mt-1">
            Assign verticals to define what this tenant can offer. One primary vertical is required for onboarding.
          </p>
        </div>
        <Button onClick={loadData} variant="secondary" disabled={isLoading}>
          <FiRefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && <ErrorState title="Operation failed" message={errorMessage} />}

      {/* Assigned Verticals */}
      <section className="rounded-lg border border-border p-6 space-y-4">
        <h3 className="text-lg font-medium">Assigned Verticals ({assignedVerticals.length})</h3>

        {assignedVerticals.length === 0 ? (
          <p className="text-content-muted text-center py-8">No verticals assigned yet. Add one below.</p>
        ) : (
          <div className="space-y-3">
            {assignedVerticals.map((vertical) => (
              <div key={vertical.verticalCode} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-border bg-background">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${vertical.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                  <div>
                    <p className="font-medium text-content">{vertical.name}</p>
                    <p className="text-sm text-content-muted">Code: {vertical.verticalCode}</p>
                  </div>
                  {vertical.isPrimary && (
                    <Badge tone="positive" className="ml-2">
                      <FiStar className="h-3 w-3 mr-1" /> Primary
                    </Badge>
                  )}
                  {vertical.status === 'disabled' && (
                    <Badge tone="neutral" className="ml-2">Disabled</Badge>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {!vertical.isPrimary && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleSetPrimary(vertical.verticalCode)}
                      disabled={isUpdating === vertical.verticalCode}
                    >
                      <FiStar className="h-3 w-3 mr-1" /> Set Primary
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleToggleStatus(vertical)}
                    disabled={isUpdating === vertical.verticalCode}
                  >
                    {vertical.status === 'active' ? (
                      <>
                        <FiX className="h-3 w-3 mr-1" /> Disable
                      </>
                    ) : (
                      <>
                        <FiCheck className="h-3 w-3 mr-1" /> Enable
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {assignedPrimary && (
          <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200">
            <p className="text-sm text-green-800 flex items-center gap-2">
              <FiCheck className="h-4 w-4" />
              Primary vertical: <strong>{assignedPrimary.name}</strong> — onboarding requirement satisfied.
            </p>
          </div>
        )}

        {!assignedPrimary && assignedVerticals.length > 0 && (
          <div className="mt-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
            <p className="text-sm text-yellow-800 flex items-center gap-2">
              <FiAlertCircle className="h-4 w-4" />
              No primary vertical set. Select one above to satisfy onboarding requirement.
            </p>
          </div>
        )}
      </section>

      {/* Available Verticals */}
      <section className="rounded-lg border border-border p-6 space-y-4">
        <h3 className="text-lg font-medium">Available Verticals ({availableVerticals.length})</h3>

        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            label="Vertical Code"
            value={newVerticalCode}
            onChange={(e) => setNewVerticalCode(e.currentTarget.value.toLowerCase())}
            placeholder="e.g., clothing, salon, bakery"
            className="flex-1"
          />
          <label className="flex items-center gap-2 self-end cursor-pointer">
            <input
              type="checkbox"
              checked={newVerticalPrimary}
              onChange={(e) => setNewVerticalPrimary(e.currentTarget.checked)}
              className="rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-content">Set as primary</span>
          </label>
          <Button
            onClick={handleAssign}
            disabled={isAssigning || !newVerticalCode.trim()}
            loading={isAssigning}
            className="self-end"
          >
            <FiPlus className="h-4 w-4 mr-2" />
            Assign
          </Button>
        </div>

        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-content-muted">Show all available verticals</summary>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 max-h-60 overflow-y-auto">
            {availableVerticals
              .filter(v => !assignedVerticals.some(av => av.verticalCode === v.code))
              .map((vertical) => (
                <button
                  key={vertical.code}
                  type="button"
                  onClick={() => {
                    setNewVerticalCode(vertical.code);
                    setNewVerticalPrimary(false);
                  }}
                  className="p-3 rounded-lg border border-border bg-background hover:bg-muted/50 text-left transition-colors"
                >
                  <p className="font-medium text-content">{vertical.name}</p>
                  <p className="text-xs text-content-muted font-mono">{vertical.code}</p>
                </button>
              ))}
          </div>
        </details>
      </section>
    </div>
  );
}